/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as moment from 'moment';
import * as _ from 'lodash';

import {ADN_TARGETING_TYPES} from './targeting-constants';
import {TargetingHelper} from './targeting-helper';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {TargetingTabController} from './targeting-tab-controller';

export interface TargetingEditorOptions {
  targeting?: any;
  modelLineItem?: any;
  pageType?: string;
  reachEnabled?: string;
  availableTargets?: any;
  mandatoryTargets?: any;
  overridableTargeting?: any;
  targetingMode?: string;
  lineItemType?: string;
  isLockedDown?: boolean;
}

export interface TargetingEditorResult {
  wc: any;
  overrides: any;
  reset: boolean;
}

export interface TargetingEditorInitContext {
  LocalNetworkProfile: any;
  LocalUserPermissions: any;
  localUserProfile: any;
}

export class TargetingEditorState {
  targeting: any;
  targets: Record<string, any>;
  targetEntries: Array<{key: string; target: any}> = [];
  targetingSizes: Record<string, number> = {};
  matchingGroups: any[] = [];
  matchingGrouped: Record<string, number> = {};
  mandatoryTargets: string[] = [];
  overridableTargeting: any;
  modelLineItem: any;
  pageType = '';
  reachEnabled: string | undefined;
  showReach = {enabled: false};
  targetingMode = 'NORMAL';
  lineItemType = 'NORMAL';
  isLockedDown = false;
  isSelfServiceOwn = false;
  isSpecialUser = false;
  underModel: any;
  resetObj = {reset: false};
  view = 'SELECTIONS';
  tabController: TargetingTabController;
  callbackHook: {onReachButtonClicked: () => Promise<any>};

  constructor(options: TargetingEditorOptions, deps: TargetingEditorInitContext) {
    this.targeting = _.cloneDeep(options.targeting) || {};
    _.forEach(ADN_TARGETING_TYPES, (tt: any) => {
      this.targeting[tt.targets] = this.targeting[tt.targets] || (tt.targetingLength ? [] : {});
    });

    this.pageType = options.pageType || '';
    this.modelLineItem = options.modelLineItem;
    this.reachEnabled = options.reachEnabled;
    this.showReach = {enabled: !!options.reachEnabled};
    this.targetingMode = options.targetingMode || 'NORMAL';
    this.lineItemType = options.lineItemType || 'NORMAL';
    this.isLockedDown = !!options.isLockedDown;
    this.isSelfServiceOwn = deps.LocalUserPermissions.isSelfServiceOwn();
    this.isSpecialUser = deps.localUserProfile.isSpecialUser();
    this.overridableTargeting = options.overridableTargeting ? _.cloneDeep(options.overridableTargeting) : null;

    this.initTargets(options, deps);
    this.initMatchingGroups();
    this.tabController = new TargetingTabController(() => this.calcSizes());
    this.callbackHook = {
      onReachButtonClicked: () => this.tabController.tabBeforeSubmit()
    };
    this.calcSizes();
  }

  calcSizes(): void {
    _.forEach(ADN_TARGETING_TYPES, (val: any) => {
      this.targetingSizes[val.targets] = TargetingHelper.getSizeOfTargetingTypes(this.targeting[val.targets], val.targets);
    });
  }

  setView(view: string): void {
    this.view = view;
  }

  onTargetTabSelect(target: any): void {
    if (target && target.widget) {
      this.tabController.onTabSelect(target.widget);
    }
  }

  private initTargets(options: TargetingEditorOptions, deps: TargetingEditorInitContext): void {
    const omitTypes = ['publisher'];
    if (!deps.LocalNetworkProfile.isSemanticPlatform()) {
      omitTypes.push('semantic');
    }
    if (!deps.LocalNetworkProfile.isSimilarKeywordsPlatform()) {
      omitTypes.push('similarKeyword');
    }

    let targets: Record<string, any> = options.availableTargets
      ? _.cloneDeep(options.availableTargets)
      : _.omit(_.cloneDeep(ADN_TARGETING_TYPES), omitTypes);

    if (deps.LocalNetworkProfile.isPureMarketplacePlatform() && targets.adUnit && targets.site) {
      const filtered = _.filter(_.map(ADN_TARGETING_TYPES, (_val: any, key: string) => key), (field) => {
        return field !== 'site' && field !== 'adUnit' && field !== 'publisher' && targets[field];
      });
      const reorderedTypes = _.filter(['publisher', 'site', 'adUnit', 'semantic'], (key) => {
        return !options.availableTargets || targets[key];
      });
      targets = _.pick(ADN_TARGETING_TYPES as any, reorderedTypes.concat(filtered) as any);
    }
    if (!LocalNetworkInfo.obtain().showSiteCountryTargeting()) {
      delete targets.siteCountry;
    }
    if (Object.keys(deps.LocalNetworkProfile.getAudienceFees()).length === 0 || this.pageType !== 'lineItem') {
      delete targets.thirdPartyAudience;
    }

    _.forEach(targets, (t: any) => {
      if (t.params && Object.prototype.hasOwnProperty.call(t.params, 'lineItemStartDate') && this.modelLineItem && this.modelLineItem.startDate) {
        t.params.lineItemStartDate = moment(this.modelLineItem.startDate).toISOString();
      }
      if (t.params && Object.prototype.hasOwnProperty.call(t.params, 'advertiserReference') && this.modelLineItem && this.modelLineItem.advertiserReference) {
        t.params.advertiserReference = this.modelLineItem.advertiserReference;
      }
      t.params = t.params || {};
      t.params.mandatory = !!_.get(options.mandatoryTargets, [t.id]);
    });

    this.targets = targets;
    this.targetEntries = _.map(targets, (target, key) => ({key, target}));
    this.mandatoryTargets = _.filter(targets, (t: any) => _.get(t, ['params', 'mandatory'])).map((t: any) => t.id);
  }

  private initMatchingGroups(): void {
    _.forEach(this.targeting, (t: any, k: string) => {
      const ref = _.isArray(t) ? _.get(t, [0, 'matchingGroup'], 0) : t && t.matchingGroup;
      if (ref) {
        for (let i = 0; i < ref; i++) {
          this.matchingGroups[i] = this.matchingGroups[i] || {};
          this.matchingGroups[i].mgs = this.matchingGroups[i].mgs || [];
          this.matchingGroups[i].mgs.push(k);
        }
      }
    });

    let refMatchingGroups = _.cloneDeep(this.matchingGroups);
    _.forEach(refMatchingGroups, (mg) => {
      mg.mgs = _.filter(mg.mgs, (m) => m);
    });
    refMatchingGroups = _.filter(refMatchingGroups, (val) => val.mgs && val.mgs.length > 0 && val.mgs[0]);
    _.forEach(this.targeting, (t: any) => {
      if (_.isArray(t)) {
        _.forEach(t, (tEl) => {
          delete tEl.matchingGroup;
        });
      } else if (t) {
        delete t.matchingGroup;
      }
    });
    this.matchingGrouped = {};
    _.forEach(refMatchingGroups, (val, index) => {
      _.forEach(val.mgs, (mg: string) => {
        if (_.isArray(this.targeting[mg])) {
          _.forEach(this.targeting[mg], (tEl) => {
            tEl.matchingGroup = index + 1;
          });
        } else if (this.targeting[mg]) {
          this.targeting[mg].matchingGroup = index + 1;
        }
        this.matchingGrouped[mg] = index + 1;
      });
    });
  }
}
